import type { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { AnalyticsItemListImpression } from "@/components/analytics-item-list-impression";
import { CategoryEmptyState } from "@/components/category-empty-state";
import { CategoryPageIntro } from "@/components/category-page-intro";
import { MenuPageCanvas } from "@/components/menu-page-canvas";
import { PageSeoHead } from "@/components/page-seo-head";
import { ProductCard } from "@/components/product-card";
import {
  getFooter,
  getMenuPage,
  getNavigation,
  getProductsByCategory,
} from "@/lib/api";
import {
  buildBreadcrumbList,
  buildCategoryItemList,
  buildMenuSectionSchema,
} from "@/lib/seo-schema";
import type {
  StorefrontLayoutVariant,
  StorefrontShellData,
} from "@/lib/storefront-shell-types";
import {
  getStorefrontCanonicalOrigin,
  resolveStorefrontSiteCode,
  type StorefrontSiteCode,
} from "@/lib/storefront-site";
import type {
  CategorySummary,
  ContentBlock,
  NavigationItem,
  ProductListItem,
} from "@/lib/storefront-types";

type MenuCategoryPageProps = {
  category: CategorySummary;
  blocks: ContentBlock[];
  layoutVariant: StorefrontLayoutVariant;
  navigationItems: NavigationItem[];
  products: ProductListItem[];
  shellData: StorefrontShellData;
  siteCode: StorefrontSiteCode;
};

export const getServerSideProps: GetServerSideProps<MenuCategoryPageProps> = async (
  context,
) => {
  const siteCode = resolveStorefrontSiteCode({
    configuredSiteCode: process.env.STOREFRONT_SITE,
    hostname: context.req.headers.host,
  });
  const categorySlug = context.params?.categorySlug;
  if (typeof categorySlug !== "string" || !categorySlug.trim()) {
    return { notFound: true };
  }

  const [navigation, menuPage, categoryPage, footer] = await Promise.all([
    getNavigation(siteCode),
    getMenuPage(siteCode),
    getProductsByCategory(categorySlug, siteCode),
    getFooter(siteCode),
  ]);

  if (!categoryPage) {
    return { notFound: true };
  }

  return {
    props: {
      category: categoryPage.category,
      blocks: categoryPage.blocks,
      layoutVariant: "immersive",
      navigationItems: navigation.items,
      products: categoryPage.products,
      shellData: {
        footer,
        menuCategories: menuPage.categories,
        currentPageTitle: categoryPage.category.name,
        loginRegisterInsertAfter: navigation.loginRegisterInsertAfter,
      },
      siteCode,
    },
  };
};

export default function MenuCategoryPage({
  blocks,
  category,
  products,
  siteCode,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const origin = getStorefrontCanonicalOrigin(siteCode);
  const structuredData: object[] = [
    buildBreadcrumbList(origin, [
      { name: "Home", path: "/" },
      { name: "Menu", path: "/menu" },
      { name: category.name, path: category.url },
    ]),
    buildCategoryItemList(origin, category, products),
  ];
  // MenuSection is restaurant vocabulary — Frozen keeps shop (ItemList) semantics.
  if (siteCode === "asorockfood") {
    structuredData.push(buildMenuSectionSchema(origin, category, products));
  }

  return (
    <>
      <PageSeoHead
        siteCode={siteCode}
        seo={category.seo}
        structuredData={structuredData}
      />
      <MenuPageCanvas
        blocks={blocks}
        outerClassName="pb-10 sm:pb-12 lg:pb-14"
        innerClassName="mx-auto flex max-w-6xl flex-col gap-8 px-4 pt-6 sm:gap-10 sm:px-6 sm:pt-8"
        topSection={
          <CategoryPageIntro
            category={category}
            productCount={products.length}
            siteCode={siteCode}
          />
        }
      >
        <AnalyticsItemListImpression
          itemListId={category.slug}
          itemListName={category.name}
          products={products}
        />
        {products.length > 0 ? (
          <section className="grid gap-5 sm:grid-cols-2 lg:gap-6">
            {products.map((product) => (
              <ProductCard
                key={product.key}
                analyticsItemListId={category.slug}
                analyticsItemListName={category.name}
                product={product}
              />
            ))}
          </section>
        ) : (
          <CategoryEmptyState />
        )}
      </MenuPageCanvas>
    </>
  );
}
